// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState } from 'react'
import { AlertCircle, CheckCircle2, FlaskConical, Loader2, Search } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useTableFunnels, type FunnelColumn } from '../common/TableFunnels'

type BackfillItem = api.FormularyBackfillPreview['items'][number]
// D-UI-2: a funnel on every column; each value is what the cell shows.
const BACKFILL_FUNNELS: FunnelColumn<BackfillItem>[] = [
  { key: 'name', label: 'Drug', value: c => c.name },
  { key: 'strength', label: 'Strength', value: c => c.strength },
  { key: 'ndc', label: 'NDC', value: c => c.ndc },
  { key: 'dispense_count', label: 'Dispensed', value: c => c.dispense_count },
  { key: 'linked', label: 'Reference', value: c => (c.linked ? 'matched' : 'needs review') },
]

/**
 * D-CRX-61: CareRx formulary backfill card (SaaS-admin, pharmacy detail page).
 *
 * CareRx ships no medication backfill, so a pharmacy that enables the module gets an empty
 * formulary — the enrollment door still lists their prescriptions, but specialty/REMS
 * flagging, therapy tracking and the dashboard all INNER JOIN the formulary and return
 * nothing. The module looks empty rather than broken.
 *
 * This derives the formulary from the pharmacy's OWN dispensing history. Preview first,
 * always: the operator sees the candidate count and the top drugs by dispense volume before
 * anything is written. That review step is a round-table condition (Sasha), not a nicety —
 * so Apply is deliberately unreachable until a preview has been run in this session.
 *
 * Re-runnable: drugs already in the formulary are excluded server-side, so running it again
 * after a few months' dispensing picks up only what is new.
 */
export default function PharmacyCareRxBackfillCard({ pharmacyId }: { pharmacyId: string }) {
  const [preview, setPreview] = useState<api.FormularyBackfillPreview | null>(null)
  const [applied, setApplied] = useState<api.FormularyBackfillApplied | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null)
  const [limit, setLimit] = useState(500)
  const [minCount, setMinCount] = useState(1)

  async function runPreview() {
    setBusy('preview'); setError(null); setApplied(null)
    try { setPreview(await api.previewFormularyBackfill(pharmacyId, limit, minCount)) }
    catch (e) { setError(friendlyError(e)); setPreview(null) }
    finally { setBusy(null) }
  }

  async function runApply() {
    setBusy('apply'); setError(null)
    try {
      setApplied(await api.applyFormularyBackfill(pharmacyId, limit, minCount))
      setPreview(null)   // the candidate set is consumed — force a fresh preview before another apply
    }
    catch (e) { setError(friendlyError(e)) }
    finally { setBusy(null) }
  }

  // D-UI-2: the funnels filter the FULL candidate list, then the top 20 of what matches is shown.
  const fx = useTableFunnels(preview?.items, BACKFILL_FUNNELS)
  const top = fx.rows.slice(0, 20)

  return (
    <section style={card}>
      <h3 style={header}><FlaskConical size={16} /> CareRx formulary backfill</h3>
      <p style={hint}>
        Builds this pharmacy's CareRx formulary from the drugs they have actually dispensed, ranked by
        volume. Clinical fields (category, baseline labs, re-careplan interval) are left blank — backfilled
        drugs land in the drug-config review queue for a clinician to classify. Safe to re-run; drugs already
        in the formulary are skipped.
      </p>

      <div style={row}>
        <label style={lbl}>Max drugs
          <input type="number" min={1} max={5000} value={limit}
            onChange={e => setLimit(Math.max(1, Math.min(5000, Number(e.target.value) || 1)))}
            style={input} disabled={busy !== null} />
        </label>
        <label style={lbl}>Min dispenses
          <input type="number" min={1} value={minCount}
            onChange={e => setMinCount(Math.max(1, Number(e.target.value) || 1))}
            style={input} disabled={busy !== null} />
        </label>
        <button onClick={runPreview} disabled={busy !== null} style={btn}>
          {busy === 'preview' ? <Loader2 size={14} className="spin" /> : <Search size={14} />} Preview
        </button>
        <button onClick={runApply} disabled={busy !== null || !preview || preview.candidates === 0}
          style={{ ...btn, ...(preview && preview.candidates > 0 ? btnPrimary : {}) }}
          title={preview ? '' : 'Run a preview first'}>
          {busy === 'apply' ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
          {preview ? ` Add ${preview.candidates} drugs` : ' Apply'}
        </button>
      </div>

      {error && <div style={errBox}><AlertCircle size={14} /> {error}</div>}

      {applied && (
        <div style={okBox}>
          <CheckCircle2 size={14} /> Added {applied.created} drugs to the formulary.
          {applied.truncated && ' Hit the cap — run another preview to see what is left.'}
          {' '}Unclassified drugs are waiting in the CareRx drug-config review queue.
        </div>
      )}

      {preview && (
        <>
          <div style={statRow}>
            <Stat label="Candidates" value={preview.candidates} />
            <Stat label="Auto-linked to drug reference" value={preview.auto_linkable} />
            <Stat label="Need clinical review" value={preview.needs_clinical_review} />
          </div>
          {preview.truncated && (
            <div style={warnBox}>
              <AlertCircle size={14} /> Capped at {preview.limit}. Apply this batch, then preview again for the rest.
            </div>
          )}
          {preview.candidates === 0 ? (
            <p style={hint}>Nothing to add — every drug this pharmacy dispenses is already in the formulary.</p>
          ) : (
            <>
            {fx.chips}
            <table {...fx.tableProps} style={table}>
              <thead>
                <tr><th style={th}>Drug {fx.funnel('name')}</th><th style={th}>Strength {fx.funnel('strength')}</th><th style={th}>NDC {fx.funnel('ndc')}</th>
                  <th style={{ ...th, textAlign: 'right' }}>Dispensed {fx.funnel('dispense_count')}</th><th style={th}>Reference {fx.funnel('linked')}</th></tr>
              </thead>
              <tbody>
                {top.map((c, i) => (
                  <tr key={`${c.ndc ?? c.name}-${i}`}>
                    <td style={td}>{c.name}</td>
                    <td style={td}>{c.strength ?? '—'}</td>
                    <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{c.ndc ?? '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.dispense_count}</td>
                    <td style={td}>{c.linked ? 'matched' : 'needs review'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
          {preview.candidates > top.length && (
            <p style={hint}>
              {fx.active
                ? `Showing the top ${top.length} by volume of the ${fx.rows.length} that match the filters. Apply still adds all ${preview.candidates}.`
                : `Showing the top ${top.length} by volume of ${preview.candidates}. Apply adds all of them.`}
            </p>
          )}
        </>
      )}
    </section>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={stat}>
      <div style={statValue}>{value}</div>
      <div style={statLabel}>{label}</div>
    </div>
  )
}

const card: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginTop: 16, background: '#fff' }
const header: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 6px', fontSize: 15 }
const hint: React.CSSProperties = { color: '#6b7280', fontSize: 13, margin: '0 0 12px', lineHeight: 1.5 }
const row: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }
const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: '#6b7280' }
const input: React.CSSProperties = { width: 110, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }
const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', fontSize: 13, cursor: 'pointer' }
const btnPrimary: React.CSSProperties = { background: '#111827', color: '#fff', borderColor: '#111827' }
const errBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 10px', background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 13 }
const okBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 10px', background: '#f0fdf4', color: '#166534', borderRadius: 6, fontSize: 13 }
const warnBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, padding: '8px 10px', background: '#fffbeb', color: '#92400e', borderRadius: 6, fontSize: 13 }
const statRow: React.CSSProperties = { display: 'flex', gap: 24, marginTop: 16 }
const stat: React.CSSProperties = { minWidth: 90 }
const statValue: React.CSSProperties = { fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }
const statLabel: React.CSSProperties = { fontSize: 12, color: '#6b7280' }
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', marginTop: 12, fontSize: 13 }
const th: React.CSSProperties = { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 500, fontSize: 12 }
const td: React.CSSProperties = { padding: '6px 8px', borderBottom: '1px solid #f3f4f6' }
